import { pickAppCopy, type SupportedLocale } from "@patient-watch/i18n";
import { useAppLocale } from "./LocaleContext";
import { diaryCopyEn } from "@patient-watch/i18n/webapp/en-GB/diaryCopy";
import { diaryCopyDe } from "@patient-watch/i18n/webapp/de-DE/diaryCopy";
import { diaryCopyDa } from "@patient-watch/i18n/webapp/da-DK/diaryCopy";
import { diaryCopyEnUs } from "@patient-watch/i18n/webapp/en-US/diaryCopy";
import { diaryCopyFr } from "@patient-watch/i18n/webapp/fr-FR/diaryCopy";
import { diaryCopyIt } from "@patient-watch/i18n/webapp/it-IT/diaryCopy";
import { diaryCopyEs } from "@patient-watch/i18n/webapp/es-ES/diaryCopy";

interface DiaryCopy {
    diaries: string;
    diary: string;
    diaryPeriod: string;
    toDo: string;
    results: string;
    /** Suffix for an open-ended date range, e.g. "- Now" / "- Heute". */
    toNow: string;
    edit: string;
    change: string;
    /** Header button to the page of the diary's own details (dates, side, consent), as opposed to its results. */
    details: string;
    deleteEntry: string;
    done: string;
    next: string;
    save: string;
    saving: string;
    cancel: string;
    date: string;
    time: string;
    noSteps: string;
    step: string;
    chooseStep: string;
    currently: string;
    general: string;
    procedureDetails: string;
    hospitalReferenceNumber: string;
    hospitalNumber: string;
    hospitalNumberDescription: string;
    enterHospitalNumber: string;
    startDate: string;
    procedureDate: string;
    referenceDate: string;
    referenceDateDescription: string;
    baselineDate: string;
    baselineDateDescription: string;
    patientBaselineDate: string;
    patientBaselineDateDescription: string;
    eventDateDescription: string;
    patientEventDateDescription: string;
    endDate: string;
    contactPerson: string;
    contactPersonDescription: string;
    loadingContact: string;
    side: string;
    anatomicArea: string;
    questionnaireSide: string;
    questionnaireSideDescription: string;
    notSet: string;
    trackerSettings: string;
    consent: string;
    consentForm: string;
    consentDetails: string;
    openConsent: string;
    sign: string;
    signed: string;
    signedAt: (date: string) => string;
    requiresSignature: string;
    actionRequiredSignConsent: string;
    signConsentBeforeContinuing: (count: number) => string;
    signConsentNow: string;
    detailsStillNeed: string;
    detailsStillNeedDescription: string;
    injectionSide: string;
    injectionAnatomicArea: string;
    surgerySide: string;
    shockwaveSide: string;
    shockwaveAnatomicArea: string;
    yourEntries: string;
    noDiary: string;
    noCompletedEntries: string;
    form: string;
    forms: string;
    item: string;
    items: string;
    questionnaire: string;
    questionnaires: string;
    injection: string;
    surgery: string;
    shockwave: string;
    consultation: string;
    procedure: string;
    procedures: (count: number) => string;
    task: string;
    tasks: string;
    toDoEmpty: string;
    blocked: string;
    blockedEmpty: string;
    formsLockedUntilConsentSigned: string;
    completePreviousFormFirst: (formName: string) => string;
    doneEmpty: string;
    complete: string;
    due: string;
    upToDate: string;
    allCaughtUp: string;
    formsNeedAttention: string;
    progress: string;
    completed: string;
    /** A visit this form was not needed for (for example skipped because of an earlier answer). */
    notNeeded: string;
    dueNow: string;
    waiting: string;
    locked: string;
    lockedByEarlierEntry: string;
    lockedUntilPreviousFormComplete: (formName: string) => string;
    availableLater: string;
    nextDue: string;
    dueOn: (date: string) => string;
    nextScheduledSentence: (formLabel: string, scheduleLabel: string, date: string) => string;
    noEntriesScheduled: string;
    showLess: string;
    showMore: (count: number) => string;
    showAllTimings: (count: number) => string;
    expand: string;
    submitted: (completed: number, total: number) => string;
    submittedOn: (date: string) => string;
    noDataYet: string;
    noDataDescription: string;
    notEnoughDataYet: string;
    notEnoughDataDescription: string;
    addEntry: string;
    addEntryFor: (formSlug: string, timingLabel: string) => string;
    addExtraScore: string;
    addExtraScoreTitle: string;
    addExtraScoreDescription: string;
    addForm: string;
    entryDue: (label: string) => string;
    nextEntryDueOn: (label: string, date: string) => string;
    entriesDueForForm: (count: number) => string;
    formWithEntriesDue: (formName: string, entries: number) => string;
    formsWithEntriesDue: (forms: number, entries: number) => string;
    allFormsComplete: string;
    allEntriesComplete: string;
    entriesDueNow: (count: number) => string;
    completeYourEntry: (label: string) => string;
    questionnaireDueNow: string;
    nextEntryLabel: (label: string) => string;
    waitingForNextEntry: string;
    readyLabel: (label: string) => string;
    readyToComplete: string;
    reminderText: string;
    reminderEmail: string;
    reminderTextAndEmail: string;
    reminderNone: string;
    entrySubmitted: string;
    completeWithCareTeam: string;
    formNumber: (index: number) => string;
    dateOfProcedure: (phrase: string) => string;
    wrongDate: string;
    editHere: string;
    showAnswers: string;
    hideAnswers: string;
    showAnswersDescription: string;
    entryCountLabel: (count: number) => string;
    timing: string;
    referredBy: (name: string) => string;
    performedBy: (name: string) => string;
    method: (method: string) => string;
    injectionDetails: string;
    unknown: string;
    noDateSpecified: string;
    noInjectionDetails: string;
    sideNotSpecified: string;
    areaNotSpecified: string;
    siteNotSpecified: string;
    substanceNotSpecified: string;
    blankAnswer: string;
    listEmptyTitle: string;
    listEmptyDescription: string;
    listCheckAgain: string;
    listNeedsAttention: string;
    listNeedsAttentionDescription: string;
    listOngoing: string;
    listOngoingDescription: string;
    listUpToDateDescription: string;
    listNoOngoing: string;
    listCompletedDescription: string;
    listNoCompleted: string;
    listOpenDiary: string;
    listReviewDiary: string;
}

const diaryCopy: Record<SupportedLocale, DiaryCopy> = {
    "en-GB": diaryCopyEn,
    "en-US": diaryCopyEnUs,
    "de-DE": diaryCopyDe,
    "da-DK": diaryCopyDa,
    "fr-FR": diaryCopyFr,
    "it-IT": diaryCopyIt,
    "es-ES": diaryCopyEs,
};

export function useDiaryCopy() {
    const { locale } = useAppLocale();
    return pickAppCopy(diaryCopy, locale);
}

export function getDiaryCopy(locale: SupportedLocale) {
    return pickAppCopy(diaryCopy, locale);
}
